/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { RefreshControl, ScrollView, Text, View } from 'react-native';
import { styles } from './ReportsScreen.styles';
import { spacing } from '../../shared/theme/spacing';
import type { ReportsScreenViewModel } from './useReportsScreen';
import type { ReportsSection } from './reportsScreen.constants';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ReportsMetricTile } from './components/ReportsMetricTile';
import { ReportsErrorRetryCard } from './components/ReportsErrorRetryCard';
import { ReportsPeriodFilterPanel } from './components/ReportsPeriodFilterPanel';
import { ReportsDataSourceFooter } from './components/ReportsDataSourceFooter';
import { useReportsVentasLayout } from './reportsVentasLayout';
import { computeFullProfitLoss, expensesShareOfSalesLabel } from './reportsScreen.utils';
import { formatMoney } from '../../shared/format/public-api';
import { ReportsTreasuryPeriodSection } from './components/ReportsTreasuryPeriodSection';

type KpiItem = {
  key: string;
  label: string;
  value: string;
  valueTone?: 'emphasis' | 'positive' | 'negative' | 'warning' | 'default';
  sub?: string;
  onPress?: () => void;
  disabled?: boolean;
  accessibilityLabel?: string;
};

function safeDeltaPct(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / previous;
}

function formatDelta(pct: number | null): string {
  if (pct == null) return '';
  const sign = pct >= 0 ? '↑' : '↓';
  return ` · ${sign} ${Math.abs(pct * 100).toFixed(1)}% vs anterior`;
}

export function ReportsResumenView({ vm }: { vm: ReportsScreenViewModel }) {
  const ventasLayout = useReportsVentasLayout();
  const { layoutWide } = ventasLayout;
  const periodDataLoading = vm.loading;
  const tileLayout = layoutWide ? 'wide' : 'default';

  const currentSales = vm.periodSalesSnapshot?.salesTotal ?? 0;
  const periodSalesCount = vm.periodSalesSnapshot?.salesCount ?? 0;
  const ticketPromedio = periodSalesCount > 0 ? currentSales / periodSalesCount : 0;
  /** KPIs de margen: `null` = no cargados (loading o error) — no renderizar $0 falsos. */
  const margenKpisLoaded = vm.periodRentabilidadKpis != null;
  const marginTotal = vm.periodRentabilidadKpis?.rentabilidad.margenReposicionTotal ?? 0;
  const costoTotal = vm.periodRentabilidadKpis?.rentabilidad.costoReposicionTotal ?? 0;
  const fullPl = computeFullProfitLoss(vm.profitLoss, vm.periodRentabilidadKpis?.rentabilidad);
  const expensesTotal = fullPl.expensesTotal;
  const resultNeto = fullPl.netIncome;
  const previousSales = vm.previousPeriodSalesSnapshot?.salesTotal ?? 0;
  const previousMargin = vm.previousPeriodRentabilidadKpis?.rentabilidad.margenReposicionTotal ?? 0;
  const previousSalesCount = vm.previousPeriodSalesSnapshot?.salesCount ?? 0;
  const previousTicket = previousSalesCount > 0 ? previousSales / previousSalesCount : 0;

  const salesDeltaPct = safeDeltaPct(currentSales, previousSales);
  const marginDeltaPct = safeDeltaPct(marginTotal, previousMargin);
  const ticketDeltaPct = safeDeltaPct(ticketPromedio, previousTicket);

  const handleGoToSection = (section: ReportsSection) => () => vm.setActiveSection(section);

  // Share de gastos sobre ventas: con ventas = 0 el cociente no existe —
  // mostrar '—' en vez de una magnitud ficticia (Math.max(1, sales) inflaba
  // gastos a % astronómicos, ej. 233256.0%).
  const expensesShareLabel = expensesShareOfSalesLabel(fullPl.expensesTotal, currentSales);

  const margenKpi: KpiItem = {
    key: 'margen',
    label: 'Margen de productos',
    value: `${formatMoney(marginTotal)}`,
    valueTone: marginTotal >= 0 ? 'positive' : 'negative',
    sub: (costoTotal > 0 ? `Costo reposición ${formatMoney(costoTotal)}` : 'Sin costo de reposición cargado') + formatDelta(marginDeltaPct),
    onPress: handleGoToSection('ventas'),
    accessibilityLabel: 'Ir a margen',
  };
  const resultadoKpi: KpiItem = {
    key: 'resultado',
    label: 'Resultado neto (rentabilidad)',
    value: `${formatMoney(resultNeto)}`,
    valueTone: resultNeto >= 0 ? 'positive' : 'negative',
    sub: `Margen ${(fullPl.grossMarginPct * 100).toFixed(1)}% · Neto ${(fullPl.netIncomePct * 100).toFixed(1)}%`,
    onPress: handleGoToSection('ventas'),
    accessibilityLabel: 'Ir a rentabilidad',
  };

  const executiveKpis: KpiItem[] = [
    {
      key: 'facturacion',
      label: 'Facturación',
      value: `${formatMoney(currentSales)}`,
      valueTone: 'emphasis',
      sub: `${periodSalesCount} ventas · Ticket promedio ${formatMoney(Math.round(ticketPromedio))}${formatDelta(salesDeltaPct)}`,
      onPress: handleGoToSection('ventas'),
      accessibilityLabel: 'Ir a ventas',
    },
    ...(margenKpisLoaded ? [margenKpi] : []),
    {
      key: 'gastos',
      label: 'Gastos del período',
      value: `${formatMoney(expensesTotal)}`,
      valueTone: expensesTotal > 0 ? 'warning' : 'default',
      sub: vm.expensesSummary
        ? `${vm.expensesSummary.byCategory.length} categorías · ${expensesShareLabel}`
        : 'Módulo Gastos no activo',
      onPress: handleGoToSection('operacion'),
      disabled: !vm.expensesSummary,
      accessibilityLabel: 'Ir a gastos del período',
    },
    ...(margenKpisLoaded ? [resultadoKpi] : []),
    {
      key: 'ticket',
      label: 'Ticket promedio',
      value: formatMoney(Math.round(ticketPromedio)),
      sub: `${periodSalesCount} ventas en el período${formatDelta(ticketDeltaPct)}`,
      onPress: handleGoToSection('ventas'),
      accessibilityLabel: 'Ir a ventas',
    },
  ];

  return (
    <ScrollView
      style={styles.reportsContentScroll}
      contentContainerStyle={[styles.content, { flexGrow: 1, paddingBottom: spacing.xxl }]}
      accessibilityState={{ busy: periodDataLoading }}
      refreshControl={
        <RefreshControl
          refreshing={vm.refreshing}
          onRefresh={vm.onRefresh}
          colors={[ticketColors.ink2]}
          tintColor={ticketColors.ink2}
        />
      }
    >
      <ReportsPeriodFilterPanel
        vm={vm}
        disabled={periodDataLoading}
        presetAccessibilityPrefix="Período resumen"
      />

      {vm.canShowTreasuryBlock ? (
        <ReportsTreasuryPeriodSection
          variant="compact"
          summary={vm.treasurySummary}
          loading={vm.treasuryLoading}
          error={vm.treasuryError}
          periodHint={vm.dateRangeLabel}
          paymentMethodNames={vm.paymentMethodNames}
          layoutWide={layoutWide}
          stale={periodDataLoading}
          onRetry={() => void vm.reloadTreasury({ force: true })}
          onOpenDetail={vm.handleOpenTesoreriaDetalle}
        />
      ) : null}

      <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]} testID="informes.kpi.sheet">
        <Text style={styles.kpiSheetTitle}>Rentabilidad del período</Text>
        <Text style={styles.kpiSheetHint}>{vm.dateRangeLabel}{periodDataLoading ? ' · actualizando…' : ''}</Text>
        <View style={styles.ventasIndicatorsGrid}>
          {executiveKpis.map((kpi) => (
            <ReportsMetricTile
              key={kpi.key}
              layout={tileLayout}
              label={kpi.label}
              value={kpi.value}
              valueTone={kpi.valueTone}
              sub={kpi.sub}
              onPress={kpi.onPress}
              disabled={kpi.disabled ?? false}
              accessibilityLabel={kpi.accessibilityLabel}
            />
          ))}
        </View>
        {!margenKpisLoaded ? (
          vm.rentabilidadError ? (
            <ReportsErrorRetryCard
              message={vm.rentabilidadError}
              showRetry={vm.rentabilidadErrorShowRetry}
              onRetry={() => void vm.handleLoadRentabilidad()}
              retryAccessibilityLabel="Reintentar cargar margen"
            />
          ) : vm.rentabilidadLoading || periodDataLoading ? (
            <Text style={[styles.caption, { marginTop: spacing.sm, color: ticketColors.ink3 }]}>
              Cargando margen…
            </Text>
          ) : (
            <Text style={[styles.caption, { marginTop: spacing.sm, color: ticketColors.ink3 }]}>
              Sin datos de margen. Conectate y recargá.
            </Text>
          )
        ) : null}
      </PaperSheet>

      <PaperSheet padded style={[styles.kpiSheet, periodDataLoading && styles.ventasStaleData]}>
        <Text style={styles.kpiSheetTitle}>El local</Text>
        <Text style={styles.kpiSheetHint}>{vm.branchHealth.shortLabel}{periodDataLoading ? ' · actualizando…' : ''}</Text>
        <View style={styles.ventasIndicatorsGrid}>
          <ReportsMetricTile
            layout={tileLayout}
            label="Sucursal"
            value={vm.branchHealth.shortLabel}
            valueTone={
              vm.branchHealth.status === 'red'
                ? 'negative'
                : vm.branchHealth.status === 'yellow'
                  ? 'warning'
                  : 'positive'
            }
            sub={vm.branchHealth.message}
            subNumberOfLines={2}
            warning={vm.branchHealth.status === 'yellow' || vm.branchHealth.status === 'red'}
            onPress={handleGoToSection('operacion')}
            accessibilityLabel="Ir a operación"
          />
        </View>
      </PaperSheet>

      <ReportsDataSourceFooter lastUpdatedAt={vm.lastDashboardLoadAt} />
    </ScrollView>
  );
}
